import { clamp } from "lodash-es";
import { useExpenseQuery } from "web_myplann/Api/Finances/useExpenseQuery";
import { useIncomeQuery } from "web_myplann/Api/Finances/useIncomeQuery";
import { ClientFinancialPlanExpenseNameEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	OWN_HOME_INCOME_FALLBACK,
	OWN_HOME_INCOME_FIELD,
	OWN_HOME_RENT_FALLBACK,
	OWN_HOME_RENT_FIELD,
	OWN_HOME_SAVINGS_FALLBACK,
} from "web_myplann/Inspiration/constants";
import { useResetOnOpen } from "web_myplann/Inspiration/Hooks/useResetOnOpen";

type Args = {
	open: boolean;
};

type Result = {
	rent: number;
	setRent: (value: number) => void;
	income: number;
	setIncome: (value: number) => void;
	savings: number;
	setSavings: (value: number) => void;
};

export const useOwnHomeEstimate = ({ open }: Args): Result => {
	const { data: expenseData } = useExpenseQuery();
	const { data: incomeData } = useIncomeQuery();

	const rentDefault = (() => {
		const total =
			expenseData?.data
				?.filter((e) => e.name === ClientFinancialPlanExpenseNameEnum.Rent)
				.reduce((sum, item) => sum + (item.monthlyPayment ?? 0), 0) ?? 0;
		return total > 0
			? clamp(total, OWN_HOME_RENT_FIELD.min, OWN_HOME_RENT_FIELD.max)
			: OWN_HOME_RENT_FALLBACK;
	})();

	const incomeDefault = (() => {
		const total =
			incomeData?.data?.reduce((sum, item) => sum + (item.amount ?? 0), 0) ?? 0;
		return total > 0
			? clamp(total, OWN_HOME_INCOME_FIELD.min, OWN_HOME_INCOME_FIELD.max)
			: OWN_HOME_INCOME_FALLBACK;
	})();

	const [rent, setRent] = useResetOnOpen(open, rentDefault);
	const [income, setIncome] = useResetOnOpen(open, incomeDefault);
	const [savings, setSavings] = useResetOnOpen(open, OWN_HOME_SAVINGS_FALLBACK);

	return { rent, setRent, income, setIncome, savings, setSavings };
};
